import * as React from "react"
import { Checkbox, cn, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { Opportunity, OpportunityAction } from "../../../types/opportunities.types"
import { HighlightText } from "../../HighlightText"
import { getDualSpendDisplay } from "../../../lib/dual-spend-display"
import { WithinBucketText } from "../../DualSpendValue"

const ACTION_STYLES: Record<OpportunityAction, string> = {
  scale: "border border-border bg-background text-foreground/70",
  launch: "border border-success-border bg-success-subtle text-success-subtle-foreground",
  test: "border border-warning-border bg-warning-subtle text-warning-subtle-foreground",
}

function MetricRow({
  label,
  perDataset,
  totalCreatives,
  barColor,
  barWidth,
  delay,
  tooltip,
  feature,
  subFeature,
}: {
  label: string
  perDataset: number | null
  totalCreatives: number | null
  barColor: string
  barWidth: number
  delay: number
  tooltip?: string
  feature: string
  subFeature: string
}) {
  const display = getDualSpendDisplay(perDataset, totalCreatives, 1)

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-[4.5rem] shrink-0 text-muted-foreground">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", barColor)}
          style={{
            width: `${barWidth}%`,
            transition: `width 650ms cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
          }}
        />
      </div>
      <span className="min-w-[4.5rem] shrink-0 text-right tabular-nums text-muted-foreground">
        <span className="block">{display.primary}</span>
        {display.secondaryValue && (
          <span className="block text-[10px] leading-tight text-muted-foreground/70">
            <WithinBucketText value={display.secondaryValue} feature={feature} subFeature={subFeature} />
          </span>
        )}
      </span>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help" tabIndex={0} aria-label={tooltip}>
              <Icon name="info" size="xs" className="text-muted-foreground/50" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        /* Reserve space so rows stay aligned when some have the icon and some don't */
        <span className="w-3.5 shrink-0" />
      )}
    </div>
  )
}

export interface OpportunityCardProps {
  opportunity: Opportunity
  isSelected?: boolean
  onSelect?: (id: string, selected: boolean) => void
  onClick?: (opportunity: Opportunity) => void
  search?: string
  mountDelay?: number
}

export function OpportunityCard({ opportunity, isSelected, onSelect, onClick, search = "", mountDelay = 0 }: OpportunityCardProps) {
  const { t } = useTranslation()
  const actionStyle = opportunity.action ? ACTION_STYLES[opportunity.action] : null

  const yourSpendDisplay = getDualSpendDisplay(opportunity.spendPerDataset, opportunity.spend, 1)
  const benchmarkSpendDisplay = getDualSpendDisplay(
    opportunity.benchmarkSpendPerDataset,
    opportunity.benchmarkSpend,
    1,
  )
  const competitorSpendDisplay = getDualSpendDisplay(
    opportunity.competitorSpendPerDataset,
    opportunity.competitorSpend,
    1,
  )

  const maxSpend = Math.max(
    yourSpendDisplay.barValue ?? 0,
    benchmarkSpendDisplay.barValue ?? 0,
    competitorSpendDisplay.barValue ?? 0,
  )
  const safeMax = maxSpend === 0 ? 1 : maxSpend

  // Single double-rAF for all bars — first frame paints width:0 via CSS start value,
  // second frame kicks the transition to the real value with staggered delays per bar.
  const [barsReady, setBarsReady] = React.useState(false)
  React.useEffect(() => {
    let id2: number
    const id = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setBarsReady(true))
    })
    return () => { cancelAnimationFrame(id); cancelAnimationFrame(id2) }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onClick?.(opportunity)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(opportunity)}
      onKeyDown={handleKeyDown}
      className={cn(
        "group/opp-card relative flex h-[320px] flex-col overflow-hidden rounded-md border bg-card p-4 text-left",
        "transition-[transform,box-shadow,border-color] duration-200 ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "cursor-pointer hover:shadow-[0_6px_24px_rgba(0,0,0,0.09),0_2px_8px_rgba(0,0,0,0.05)]",
        isSelected
          ? "border-primary/40 shadow-[0_0_0_1px_hsl(var(--primary)/0.2)]"
          : "border-border/60 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)] hover:border-primary/25",
      )}
      aria-label={opportunity.topicTitle}
    >
      {/* Hover accent bar */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[2px] rounded-t-md bg-gradient-to-r from-transparent via-primary/45 to-transparent opacity-0 transition-opacity duration-200 group-hover/opp-card:opacity-100"
        aria-hidden
      />

      {/* Top-right checkbox affordance — generous hit target avoids misclicks onto the card */}
      {onSelect && (
        <div
          className={cn(
            "absolute right-1.5 top-1.5 z-[1] flex h-9 w-9 items-center justify-center rounded-lg",
            "transition-[opacity] duration-150 motion-reduce:transition-none",
            isSelected ? "opacity-100" : "opacity-0 group-hover/opp-card:opacity-100",
          )}
        >
          <Checkbox
            checked={!!isSelected}
            onCheckedChange={(value) => onSelect(opportunity.id, !!value)}
            aria-label={t("opportunities.table.selectRow")}
            onClick={(e) => e.stopPropagation()}
            className="size-[22px]"
          />
        </div>
      )}

      {/* Header: action badge + feature, padded right of the absolute checkbox */}
      <div className={cn("flex min-w-0 flex-wrap items-center gap-2", onSelect && "pr-10")}>
        {opportunity.action ? (
          <span className={cn("shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold leading-tight", actionStyle)}>
            {t(`opportunities.actions.${opportunity.action}`)}
          </span>
        ) : (
          <span className="shrink-0 text-[11px] font-semibold leading-tight text-muted-foreground/50" aria-label={t("opportunities.actions.none")}>
            —
          </span>
        )}
        {opportunity.feature && (
          <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            {opportunity.feature}
          </span>
        )}
      </div>

      {/* Title */}
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="mt-2.5 truncate text-sm font-semibold leading-snug text-foreground">
            <HighlightText text={opportunity.topicTitle} query={search} mountDelay={mountDelay} />
          </p>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{opportunity.topicTitle}</TooltipContent>
      </Tooltip>

      {/* Description */}
      {opportunity.topicDescription && (
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="mt-1 truncate text-xs leading-relaxed text-muted-foreground">
              <HighlightText text={opportunity.topicDescription} query={search} mountDelay={mountDelay} />
            </p>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{opportunity.topicDescription}</TooltipContent>
        </Tooltip>
      )}

      {/* Spend metrics — bars stagger 100ms apart */}
      <div className="mt-3.5 flex flex-col gap-1.5">
        <MetricRow
          label={t("opportunities.card.you")}
          perDataset={opportunity.spendPerDataset}
          totalCreatives={opportunity.spend}
          barColor="bg-muted-foreground/30"
          barWidth={barsReady && yourSpendDisplay.barValue !== null ? ((yourSpendDisplay.barValue ?? 0) / safeMax) * 100 : 0}
          delay={0}
          tooltip={yourSpendDisplay.unavailable ? t("opportunities.drawer.spendUnavailableTooltip") : undefined}
          feature={opportunity.feature}
          subFeature={opportunity.subFeature}
        />
        <MetricRow
          label={t("opportunities.card.benchmark")}
          perDataset={opportunity.benchmarkSpendPerDataset}
          totalCreatives={opportunity.benchmarkSpend}
          barColor="bg-primary"
          barWidth={barsReady && benchmarkSpendDisplay.barValue !== null ? ((benchmarkSpendDisplay.barValue ?? 0) / safeMax) * 100 : 0}
          delay={100}
          tooltip={benchmarkSpendDisplay.unavailable ? t("opportunities.drawer.spendUnavailableTooltip") : undefined}
          feature={opportunity.feature}
          subFeature={opportunity.subFeature}
        />
        <MetricRow
          label={t("opportunities.card.competitors")}
          perDataset={opportunity.competitorSpendPerDataset}
          totalCreatives={opportunity.competitorSpend}
          barColor="bg-[var(--competitor)]"
          barWidth={barsReady && competitorSpendDisplay.barValue !== null ? ((competitorSpendDisplay.barValue ?? 0) / safeMax) * 100 : 0}
          delay={200}
          tooltip={competitorSpendDisplay.unavailable ? t("opportunities.drawer.spendUnavailableTooltip") : undefined}
          feature={opportunity.feature}
          subFeature={opportunity.subFeature}
        />
      </div>

      {/* ADGE SUGGESTS */}
      {opportunity.suggestion && (
        <div className="mt-3.5 border-t border-dashed border-border/50 pt-3">
          <div className="flex items-center gap-1.5">
            <Icon name="sparkles" size="xs" className="shrink-0 text-primary/60" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-primary/60">
              {t("opportunities.card.adgeSuggests")}
            </span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="mt-1 truncate text-xs leading-relaxed text-muted-foreground">
                <HighlightText text={opportunity.suggestion} query={search} mountDelay={mountDelay} />
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{opportunity.suggestion}</TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* REJECTION REASON */}
      {opportunity.rejectionReason && (
        <div className="mt-3.5 border-t border-dashed border-destructive/20 pt-3">
          <div className="flex items-center gap-1.5">
            <Icon name="x-circle" size="xs" className="shrink-0 text-destructive/60" />
            <span className="text-[10px] font-semibold uppercase tracking-widest text-destructive/60">
              {t("opportunities.table.rejectionReason")}
            </span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="mt-1 truncate text-xs leading-relaxed text-muted-foreground">
                {opportunity.rejectionReason}
              </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{opportunity.rejectionReason}</TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

export function OpportunityCardSkeleton() {
  return (
    <div className="relative flex h-[320px] flex-col overflow-hidden rounded-md border bg-card p-4">
      {/* Shimmer sweep */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-md" aria-hidden>
        <div
          className="absolute inset-y-0 -left-1/2 w-1/2 animate-[osc-shimmer-sweep_1.6s_ease-in-out_infinite]"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, color-mix(in oklch, var(--foreground) 4%, transparent) 40%, color-mix(in oklch, var(--foreground) 7%, transparent) 50%, color-mix(in oklch, var(--foreground) 4%, transparent) 60%, transparent 100%)",
          }}
        />
      </div>

      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="h-5 w-14 rounded-md bg-muted" />
        <div className="h-3 w-20 rounded bg-muted" />
      </div>

      {/* Title */}
      <div className="mt-2.5 h-4 w-4/5 rounded bg-muted" />

      {/* Description */}
      <div className="mt-1 h-3 w-3/4 rounded bg-muted" />

      {/* Metric rows */}
      <div className="mt-3.5 flex flex-col gap-2">
        {[80, 60, 90].map((w, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="h-3 w-[4.5rem] rounded bg-muted" />
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-muted-foreground/10" style={{ width: `${w}%` }} />
            </div>
            <div className="h-3 w-10 rounded bg-muted" />
          </div>
        ))}
      </div>

      {/* Suggestion */}
      <div className="mt-3.5 border-t border-dashed border-border/50 pt-3">
        <div className="h-3 w-24 rounded bg-muted" />
        <div className="mt-1.5 h-3 w-full rounded bg-muted" />
      </div>
    </div>
  )
}
